import React, { useState, useEffect } from 'react';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { format, parseISO } from 'date-fns';
import request from '@nineeng/request';

const TaskApproval = (props) => {
	const {
		name,
		value,
		onChange,
		label,
		roleIds,
		disabled,
		size = 'medium',
		userUrl = '/api/auth/current-user',
	} = props;

	const isSmall = size === 'small';
	const [isLoading, setLoading] = useState(true);
	const [user, setUser] = useState();

	const handleApprovalUpdate = (status) => {
		if(!user) return;
		const approval = {
			timestamp: new Date(),
			approvedBy: {
				userId: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
			},
			status,
		};
		onChange({ target: { name, value: approval } });
	};

	useEffect(() => {
		const getUser = async () => {
			try {
				// Fetch authenticated user
				const { body: currentUser } = await request.get(userUrl);

				// Check roles (if necessary)
				let canApprove = true;
				if(roleIds) {
					canApprove = !!currentUser.roles.find(r => roleIds.includes(r.id));
				}
				if(canApprove) setUser(currentUser);
			} catch (e) {
				console.warn(e);
			}
			setLoading(false);
		};

		getUser();
	}, []);

	const formatTimestamp = () => {
		if(!value.timestamp) return null;
		if(value.timestamp instanceof Date) return format(value.timestamp, 'dd/MM/yyyy HH:mm');
		format(parseISO(value.timestamp), 'dd/MM/yyyy HH:mm');
	};

	const canApproveOrDecline = !disabled && !!user;
	const boxStyle = { textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'end', flexGrow: 1 };

	return (
		<Paper
			variant='outlined'
			sx={{
				borderColor:value.status ? value.status === 'approved' ? 'success.main' : 'error.main' : '',
				position: 'relative',
				display: 'flex',
				flexDirection: 'row',
				padding: isSmall ? 1 : 2,
				overflow: 'hidden',
				maxWidth: '100%',
				minHeight: 110,
				marginBottom: 2,
			}}
		>
			{label && <Typography variant='caption' sx={{ width: '100%' }}>{label}</Typography>}
			<Box sx={{ width: 50, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				{value ? value.status === 'approved' ? <CheckCircleIcon sx={{ fontSize: 50 }} color='success' /> : <CancelIcon sx={{ fontSize: 50}} color='error' /> : <HourglassEmptyIcon sx={{ fontSize: 50, opacity: 0.3 }} />}
			</Box>
			{value.status && <Box sx={boxStyle}>
				<Typography variant='overline'>{value.status === 'approved' ? 'Approved' : 'Declined'} by</Typography>
				<Typography variant='subtitle2'>{value.approvedBy.lastName + ' ' + value.approvedBy.firstName}</Typography>
				<Typography variant='subtitle2'>{formatTimestamp()}</Typography>
			</Box>}

			{!value && <Box sx={boxStyle}>
				{canApproveOrDecline
					? <Box sx={{ display: 'flex', gap: 2 }}>
						<Button variant='contained' color='secondary'  onClick={() => handleApprovalUpdate('approved')}>Approve</Button>
						<Button variant='contained' color='error'  onClick={() => handleApprovalUpdate('declined')}>Decline</Button>
					</Box>
					: <Tooltip title="You are not allowed to approve" placement='left'>
						<Box>
							<Button variant='outlined' color='secondary' disabled>Approve</Button>
						</Box>
					</Tooltip>
				}
			</Box>}

			<Backdrop sx={{ position: 'absolute' }} open={isLoading}><CircularProgress /></Backdrop>
		</Paper>
	);
};

TaskApproval.type = 'TaskApproval';

export default TaskApproval;
